import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MaterialImageUpload from "../../../components/MaterialImageUpload";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../utils/authContext";
import API from "../../../utils/API";
import utils from "../../../utils/utils";


export default function EventDetails(props) {
  const { showrunner } = useAuth();
  const location = useLocation();
  const initEventDetails = {
    name: "",
    date: "",
    location: "",
    venue: "",
    production: {
      poster: "",
      description: "",
      poster_portrait: ""
    }
  };
  const [eventDetails, setEventDetails] = useState(initEventDetails);
  const [prodDesc, setProdDesc] = useState("");
  const [showID, setShowID] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    const params = new URLSearchParams(location.search);
		const showID = params.get("showID");
    setShowID(showID);
    API.getShow(showID)
      .then(res => {
        console.log("res", res);
        setEventDetails(res.data);
        setProdDesc(res.data.production.description);
      });
  }

  function handleShowChange(e) {
    const { name, value, type, files, checked } = e.target;
    if (!e.target) return;

    if (type === "file") {
        setEventDetails({ ...eventDetails, [name]: files[0] });
    } else if (type === "checkbox") {
        setEventDetails({ ...eventDetails, [name]: checked });
    } else {
        setEventDetails({ ...eventDetails, [name]: value });
    }
}
  function handleProdChange(e) {
    const { name, value, type, files } = e.target;
    if (!e.target) return;
    if (type === "file") {
      const production = eventDetails.production;
      production[name] = files[0];
      setEventDetails({ ...eventDetails, production });
    } else {
      setEventDetails({ ...eventDetails, production: { ...eventDetails.production, [name]: value } });
    }
  }

  function updateEvent() {
    setLoading(true);
    const req = new FormData();

    // Add production data
    Object.keys(eventDetails.production).forEach(key => {
        if (eventDetails.production[key]) {
            req.append(`production.${key}`, eventDetails.production[key]);
        }
    });

    // Add show data
    Object.keys(eventDetails).forEach(key => {
        if (key !== 'production') {
            if (key === 'private') {
                req.append(`show.${key}`, eventDetails[key] ? "True" : "False");
            } else {
                req.append(`show.${key}`, eventDetails[key]);
            }
        }
    });

    req.append('production.description', prodDesc);

    // Add showID
    req.append('showID', showID);

    API.updateEvent(req)
        .then(res => {
            utils.sendAlert("Event updated", "success");
            props.reset(showID, showrunner.id);
        })
        .catch(err => {
            console.log("err", err);
            utils.sendAlert("Error updating event", "error");
        })
        .finally(() => {
            setLoading(false);
        });
}

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Edit Event Details
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left Column - Form Fields */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Date and Time"
                    name="date"
                    type="datetime-local"
                    value={utils.formatDateTimeLocal(eventDetails.date)}
                    onChange={handleShowChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Venue"
                    name="venue"
                    value={eventDetails.venue || ''}
                    onChange={handleShowChange}
                    placeholder="Enter venue name"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="location"
                    value={eventDetails.location || ''}
                    onChange={handleShowChange}
                    placeholder="Enter full address"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <Box sx={{ 
                    '& .ql-editor': { 
                      minHeight: '150px',
                      fontSize: '14px'
                    }
                  }}>
                    <ReactQuill 
                      theme="snow" 
                      value={prodDesc} 
                      name="description" 
                      onChange={setProdDesc} 
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="private"
                        checked={eventDetails.private || false}
                        onChange={handleShowChange}
                      />
                    }
                    label="Private Event"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column - Poster Upload */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Poster
              </Typography>
              
              {eventDetails.production?.poster_portrait ? (
                <Box sx={{ mb: 2 }}>
                  <img 
                    src={eventDetails.production.poster_portrait} 
                    alt="Event poster" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '200px',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No poster uploaded
                </Typography>
              )}
              
                <MaterialImageUpload 
                  label="Event Poster"
                  inputProps={{ 
                    placeholder: "Upload Poster", 
                    onChange: handleProdChange, 
                    name: "poster_portrait" 
                  }} 
                  name="poster_portrait" 
                  small={eventDetails.production?.poster_portrait ? true : false}
                />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Update Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={updateEvent}
          disabled={loading}
          sx={{ minWidth: '150px' }}
        >
          {loading ? <CircularProgress size={20} /> : 'Update Event'}
        </Button>
      </Box>
    </Box>
  );
}